import { Schema } from "@upserve/cold-storage";
import { Maybe } from "true-myth";
import { Money } from "@upserve/financials";
import { isEmpty, isNil } from "ramda";
import {
  TimeBoundary,
  splitShiftDate,
  buildShiftDate,
} from "helpers/transforms/blockDates";
import { IShiftWithMergedDate } from "helpers/transforms/schedule";

const { registerSchemaType, parseBySchema, encodeBySchema } = Schema;

export enum EmptyType {
  ELIMINATE_EMPTY,
  RETAIN_EMPTY,
}

function maybetype<T>(
  valueSchema: T,
  parseEmptyTypes: EmptyType = EmptyType.RETAIN_EMPTY
): Maybe<T> {
  const parse = parseBySchema<T>(valueSchema);
  const encode = encodeBySchema<T>(valueSchema);

  const schemaType = registerSchemaType(`Maybe-${parseEmptyTypes}`, {
    encoder: (value: Maybe<T>) =>
      value.match({ Just: (val: T) => encode(val), Nothing: () => null }),
    parser: (value: Schema.JSONType) => {
      if (isNil(value)) {
        return Maybe.nothing<T>();
      }
      const parsedValue = parse(value);
      if (
        parseEmptyTypes === EmptyType.ELIMINATE_EMPTY &&
        isEmpty(parsedValue)
      ) {
        return Maybe.nothing<T>();
      } else {
        return Maybe.of(parsedValue);
      }
    },

    childSchemas: [valueSchema as {}],
  });

  return schemaType();
}

// The precise format we want to use for all Money values is in cents (no decimal)
const centsSchema = registerSchemaType<Money>("Money", {
  encoder: money => {
    return money.valueOf();
  },
  parser: (value: Schema.JSONType) => {
    return typeof value === "number" ? Money(value) : Money.zero();
  },
});

// This schema is needed to handle legacy POS generating values in dollars
const dollarsSchema = registerSchemaType<Money>("Money-Legacy", {
  encoder: money => {
    return money.toDollars();
  },
  parser: (value: Schema.JSONType) => {
    if (typeof value === "number") {
      return Money.fromDollars(value);
    }
    if (typeof value === "string") {
      return Money.fromDollars(value);
    }
    return Money.zero();
  },
});

const excludeEmptySchema = registerSchemaType<string>("StringOrEmpty", {
  encoder: value => (isEmpty(value) ? undefined : value),
  parser: (value: Schema.JSONType) => {
    return (value as string) || "";
  },
});

function moneytype(units: "dollars" | "cents"): Money {
  return units === "cents" ? centsSchema() : dollarsSchema();
}

function nameSchema<T>(schema: T, name: string): T {
  Object.defineProperty(schema, "toString", {
    enumerable: false,
    value: () => name,
  });
  return Object.freeze(schema);
}

function ShiftTimeSchema(timeBoundary: TimeBoundary) {
  const schemaType = Schema.registerSchemaType<Date>(
    `ShiftTime${timeBoundary}`,
    {
      encoder: (time: Date) => splitShiftDate(time, timeBoundary),
      parser: (shift: Schema.JSONType) =>
        buildShiftDate(shift as IShiftWithMergedDate, timeBoundary),
    }
  );

  return schemaType();
}

export {
  maybetype,
  excludeEmptySchema,
  nameSchema,
  moneytype,
  ShiftTimeSchema,
};
